// DocumentRowForm.tsx
// DocumentRowForm.tsx
import React, { useEffect, useRef } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormResetField,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import { DesktopDatePicker as DatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import DeleteIcon from "@mui/icons-material/Delete"; // Import delete icon
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome"; // Import AI/LLM icon
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy"; // Import duplicate icon
import DocumentEntityInput from "./documentEntityInput";
import { FormValues, DocumentQueueFormInput } from "./types";

import {
  CLIENT_DOC_CATEGORIES,
  NON_CLIENT_DOC_CATEGORIES,
  FIRM_BUSINESS_OBJECT_ENUM,
  INVESTMENT_BUSINESS_OBJECT_ENUM,
  COMPANY_BUSINESS_OBJECT_ENUM,
  DEAL_BUSINESS_OBJECT_ENUM,
  FIRM_ENGAGEMENT_BUSINESS_OBJECT_ENUM,
  AI_PROCESSED_DOC_TYPE_IDS,
  getSiriusBaseUrl,
} from "./constants";

// LLM styling constants (matching the pattern from rcg-diligence)
const LLMBGColor = {
  "& .MuiInputBase-root": {
    backgroundColor: "rgba(201,186,232,0.5)",
  },
  "& .MuiInputBase-input": {
    backgroundColor: "transparent", // Make input transparent so container background shows through
  },
};

const LLMBGColor__Select = {
  "& .MuiInputBase-root": {
    backgroundColor: "rgba(201,186,232,0.5)",
  },
  "& .MuiSelect-select": {
    backgroundColor: "rgba(201,186,232,0.5)",
  },
};

import { useQuery } from "@apollo/client";
import {
  GET_DOCUMENT_ACCESS_LEVELS,
  GET_DOCUMENT_TYPE_ENUMS,
  GET_KNOWLEDGE_BASE_TAGS,
} from "./graphql";
import { getDateValue } from "./utils";

interface DocumentRowFormProps {
  index: number;
  control: Control<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  resetField: UseFormResetField<FormValues>;
  watch: UseFormWatch<FormValues>;
  errors: FieldErrors<FormValues>;
  onRemove: (index: number) => void;
  isUploading?: boolean; // Add this prop
  autoPopulatedFields?: string[]; // Add this prop for LLM auto-populated fields
  setAutoPopulatedFields?: React.Dispatch<React.SetStateAction<string[]>>; // Add this prop to update auto-populated fields
  isAiProcessing?: boolean; // Add this prop for AI processing state
}

const DocumentRowForm: React.FC<DocumentRowFormProps> = ({
  index,
  control,
  setValue,
  resetField,
  errors,
  watch,
  onRemove, // Add this prop
  isUploading,
  autoPopulatedFields = [],
  setAutoPopulatedFields,
  isAiProcessing = false,
}) => {
  // State for collapsible AI notes
  const [aiNotesExpanded, setAiNotesExpanded] = React.useState(true);
  // Function to remove field from auto-populated list when user manually changes it
  const handleFieldChange = (
    fieldName: string,
    originalOnChange: (...event: any[]) => void,
    isDisabled: boolean = false
  ) => {
    return (...args: any[]) => {
      // Only remove from auto-populated list if the field is not disabled (i.e., user is manually changing it)
      if (
        !isDisabled &&
        setAutoPopulatedFields &&
        autoPopulatedFields.includes(fieldName)
      ) {
        setAutoPopulatedFields(prev =>
          prev.filter(field => field !== fieldName)
        );
      }
      // Call the original onChange handler
      originalOnChange(...args);
    };
  };

  // Get values from form
  const title = watch(`queues.${index}.title`);
  const llmNotes = watch(`queues.${index}.llmNotes`);
  const llmEntityNotes = watch(`queues.${index}.llmEntityNotes`);
  const llmEntityTypeNotes = watch(`queues.${index}.llmEntityTypeNotes`);
  const llmAsOfDateNotes = watch(`queues.${index}.llmAsOfDateNotes`);
  const duplicateDocuments = watch(`queues.${index}.duplicateDocuments`);
  const publishError = watch(`queues.${index}.publishError`);

  const accessLevel = watch(`queues.${index}.accessLevel`);
  const overrideAccessLevel = watch("overrideAccessLevel");

  const { data: accessLevelsData } = useQuery(GET_DOCUMENT_ACCESS_LEVELS);
  const { data: documentTypesData } = useQuery(GET_DOCUMENT_TYPE_ENUMS, {
    variables: {
      filter: {
        documentCategoryEnumIds:
          (overrideAccessLevel || accessLevel) === 4
            ? CLIENT_DOC_CATEGORIES
            : NON_CLIENT_DOC_CATEGORIES,
        isBusinessApplicantType: false,
      },
    },
  });
  const { data: knowledgeBaseTagsData } = useQuery(GET_KNOWLEDGE_BASE_TAGS as any);

  // Global override values
  const overrideDate = watch("overrideDate");
  const overrideDocumentTypeEnumId = watch("overrideDocumentTypeEnumId");

  const accessLevelRef = useRef<number | undefined>();

  useEffect(() => {
    const previous = accessLevelRef.current;
    if (accessLevel === 4 || previous === 4) {
      resetField(`queues.${index}.documentTypeEnumId`);
    }
    accessLevelRef.current = accessLevel;
  }, [accessLevel, resetField, index]);

  // Apply override values when they change
  useEffect(() => {
    if (overrideDate) {
      setValue(`queues.${index}.date`, overrideDate, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });

      // Clear LLM auto-population styling for date
      if (setAutoPopulatedFields) {
        setAutoPopulatedFields(prev =>
          prev.filter(field => field !== `queues.${index}.date`)
        );
      }
    }
  }, [overrideDate, setValue, index, setAutoPopulatedFields]);

  useEffect(() => {
    if (overrideDocumentTypeEnumId) {
      setValue(
        `queues.${index}.documentTypeEnumId`,
        overrideDocumentTypeEnumId,
        {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        }
      );

      // Clear LLM auto-population styling for document type
      if (setAutoPopulatedFields) {
        setAutoPopulatedFields(prev =>
          prev.filter(field => field !== `queues.${index}.documentTypeEnumId`)
        );
      }
    }
  }, [overrideDocumentTypeEnumId, setValue, index, setAutoPopulatedFields]);

  useEffect(() => {
    if (overrideAccessLevel) {
      setValue(`queues.${index}.accessLevel`, overrideAccessLevel, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });

      // Clear LLM auto-population styling for access level (future-proofing)
      if (setAutoPopulatedFields) {
        setAutoPopulatedFields(prev =>
          prev.filter(field => field !== `queues.${index}.accessLevel`)
        );
      }
    }
  }, [overrideAccessLevel, setValue, index, setAutoPopulatedFields]);

  // Apply override entity values when they change
  const ENTITY_TYPE_IDS = [
    FIRM_BUSINESS_OBJECT_ENUM,
    INVESTMENT_BUSINESS_OBJECT_ENUM,
    COMPANY_BUSINESS_OBJECT_ENUM,
    DEAL_BUSINESS_OBJECT_ENUM,
    FIRM_ENGAGEMENT_BUSINESS_OBJECT_ENUM,
  ];

  const overrideEntityValues = ENTITY_TYPE_IDS.map(
    id => watch(`overrideEntities.${id}` as any)
  );

  useEffect(() => {
    for (const entityTypeId of ENTITY_TYPE_IDS) {
      const overrideValue = watch(`overrideEntities.${entityTypeId}` as any);
      if (Array.isArray(overrideValue) && overrideValue.length > 0) {
        setValue(
          `queues.${index}.entities.${entityTypeId}` as any,
          overrideValue,
          {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          }
        );

        // Clear LLM auto-population styling for this entity field
        if (setAutoPopulatedFields) {
          const fieldKey = `queues.${index}.entities.${entityTypeId}`;
          setAutoPopulatedFields(prev =>
            prev.filter(field => field !== fieldKey)
          );
        }
      }
    }
  }, [...overrideEntityValues, setValue, index, setAutoPopulatedFields]);

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2, ...(publishError ? { border: 2, borderColor: "error.main" } : {}) }}>
      <Grid container spacing={2}>
        {publishError && (
          <Grid item xs={12}>
            <Alert severity="error">{publishError}</Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle1">
                {title || `Document ${index + 1}`}
              </Typography>

              {/* Add duplicate documents indicator */}
              {duplicateDocuments && duplicateDocuments.length > 0 && (
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Duplicate Documents Found ({duplicateDocuments.length}):
                      </Typography>
                      {duplicateDocuments.map((doc: any) => (
                        <Box key={doc.id} sx={{ mb: 0.5 }}>
                          <Link
                            href={`${getSiriusBaseUrl()}/documents/${doc.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: "inherit" }}
                          >
                            {doc.name}
                          </Link>
                          {doc.date && (
                            <Typography
                              variant="caption"
                              sx={{ ml: 1, opacity: 0.7 }}
                            >
                              (
                              {getDateValue(doc.date)?.toLocaleDateString() ||
                                doc.date}
                              )
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <IconButton
                    size="small"
                    sx={{
                      ml: 1,
                      color: "warning.main",
                      "&:hover": { color: "warning.dark" },
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Add upload indicator */}
              {isUploading && (
                <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
                  <CircularProgress size={20} color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="primary">
                    Uploading...
                  </Typography>
                </Box>
              )}

              {/* Add AI processing indicator */}
              {isAiProcessing && !isUploading && (
                <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
                  <CircularProgress
                    size={20}
                    color="secondary"
                    sx={{ mr: 1 }}
                  />
                  <AutoAwesomeIcon
                    sx={{
                      color: "rgba(156, 39, 176, 0.8)",
                      fontSize: "18px",
                      mr: 0.5,
                    }}
                  />
                  <Typography variant="body2" color="secondary">
                    AI Processing...
                  </Typography>
                </Box>
              )}
            </Box>
            <IconButton
              color="error"
              onClick={() => onRemove(index)}
              aria-label="delete document"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Grid>
        {/* Entity Selection for individual document */}
        <Grid item xs={12}>
          <div data-cy={`queues-${index}-entity-selection`}>
            <DocumentEntityInput
              allowChange={true}
              index={index}
              autoPopulatedFields={autoPopulatedFields}
              setAutoPopulatedFields={setAutoPopulatedFields}
            />
          </div>
        </Grid>
        {/* Date field */}
        <Grid item xs={4}>
          <Controller
            control={control}
            name={`queues.${index}.date`}
            rules={{ required: "Date is required" }}
            render={({ field, fieldState }) => {
              const fieldName = `queues.${index}.date`;
              const isAutoPopulated = autoPopulatedFields.includes(fieldName);

              const handleDateChange = (date: Date | null) => {
                if (date === null) {
                  field.onChange(null);
                } else if (date && !isNaN(date.getTime())) {
                  // Convert to YYYY-MM-DD format
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, "0");
                  const day = String(date.getDate()).padStart(2, "0");
                  const isoDate = `${year}-${month}-${day}`;
                  field.onChange(isoDate);
                }

                // Remove from auto-populated fields if user is manually editing
                if (isAutoPopulated && setAutoPopulatedFields) {
                  setAutoPopulatedFields(prev =>
                    prev.filter(f => f !== fieldName)
                  );
                }
              };

              return (
                <Box sx={{ position: "relative" }}>
                  <Box
                    data-cy={`queues-${index}-date-picker`}
                    sx={
                      isAutoPopulated
                        ? {
                            backgroundColor: "rgba(201,186,232,0.5)",
                            borderRadius: 1,
                            p: 0,
                          }
                        : undefined
                    }
                  >
                    <DatePicker
                      label="Date"
                      value={getDateValue(field.value ?? null)}
                      name={`queues.${index}.date`}
                      onChange={handleDateChange}
                      slotProps={{
                        field: { clearable: true },
                        textField: {
                          fullWidth: true,
                          error: !!fieldState.error,
                          helperText: fieldState.error?.message || "",
                        },
                        actionBar: {
                          actions: ["clear", "today"],
                        },
                      }}
                    />
                  </Box>

                  {isAutoPopulated && (
                    <Tooltip title="Suggested by AI">
                      <AutoAwesomeIcon
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 40,
                          color: "rgba(156, 39, 176, 0.8)",
                          fontSize: "18px",
                          zIndex: 1,
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              );
            }}
          />
        </Grid>
        <Grid item xs={4}>
          {accessLevelsData && (
            <Controller
              control={control}
              name={`queues.${index}.accessLevel`}
              rules={{
                required: "Access Level is required",
              }}
              render={({ field, fieldState }) => (
                <FormControl fullWidth error={!!fieldState.error}>
                  <InputLabel>Access Level</InputLabel>
                  <Select
                    {...field}
                    label="Access Level"
                    value={field.value || ""}
                  >
                    <MenuItem value="">None</MenuItem>
                    {accessLevelsData?.documentAccessLevelList?.items?.map(
                      (level: { id: number; name: string }) => (
                        <MenuItem key={level.id} value={level.id}>
                          {level.name}
                        </MenuItem>
                      )
                    )}
                  </Select>
                  {fieldState.error && (
                    <Typography color="error" variant="caption">
                      {fieldState.error.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          )}
        </Grid>
        <Grid item xs={4}>
          {documentTypesData && (
            <Box sx={{ position: "relative" }}>
              <Controller
                control={control}
                name={`queues.${index}.documentTypeEnumId`}
                rules={{
                  required: "Document Type is required",
                }}
                render={({ field, fieldState }) => {
                  const fieldName = `queues.${index}.documentTypeEnumId`;
                  const isAutoPopulated =
                    autoPopulatedFields.includes(fieldName);
                  return (
                    <FormControl fullWidth error={!!fieldState.error}>
                      <InputLabel>Document Type</InputLabel>
                      <Select
                        {...field}
                        onChange={handleFieldChange(
                          fieldName,
                          field.onChange,
                          false
                        )}
                        label="Document Type"
                        value={field.value || ""}
                        sx={isAutoPopulated ? LLMBGColor__Select : undefined}
                      >
                        <MenuItem value="">None</MenuItem>
                        {documentTypesData?.documentTypeEnumList?.items?.map(
                          type => (
                            <MenuItem key={type.id} value={type.id}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                {type.name}
                                {AI_PROCESSED_DOC_TYPE_IDS.includes(type.id) && (
                                  <Chip label="AI Processed" size="small" color="info" variant="outlined"
                                    sx={{ height: 20, fontSize: "0.7rem" }} />
                                )}
                              </Box>
                            </MenuItem>
                          )
                        )}
                      </Select>
                      {fieldState.error && (
                        <Typography color="error" variant="caption">
                          {fieldState.error.message}
                        </Typography>
                      )}
                    </FormControl>
                  );
                }}
              />
              {autoPopulatedFields.includes(
                `queues.${index}.documentTypeEnumId`
              ) && (
                <Tooltip title="Suggested by AI">
                  <AutoAwesomeIcon
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 35,
                      color: "rgba(156, 39, 176, 0.8)",
                      fontSize: "18px",
                      zIndex: 1,
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          )}
        </Grid>
        <Grid item xs={8}>
          <Box sx={{ position: "relative" }}>
            {/* Knowledge Base Tags */}
            {knowledgeBaseTagsData && (
              <Controller
                control={control}
                name={`queues.${index}.tags`}
                render={({ field }) => {
                  const availableTags: any[] = knowledgeBaseTagsData?.knowledgeBaseTagsList?.items || [];

                  return (
                    <Autocomplete
                      multiple
                      freeSolo
                      options={availableTags}
                      getOptionLabel={(option: any) =>
                        typeof option === "string" ? option : option.name
                      }
                      isOptionEqualToValue={(option: any, value: any) =>
                        typeof option === "string" || typeof value === "string"
                          ? option === value
                          : option.id === value.id
                      }
                      value={field.value || []}
                      onChange={(_, newValue) => {
                        field.onChange(newValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Knowledge Base Tags"
                          placeholder="Select or create tags..."
                        />
                      )}
                      renderTags={(tagValue: any[], getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            label={typeof option === "string" ? option : option.name}
                            {...getTagProps({ index })}
                            key={typeof option === "string" ? option : option.id}
                            size="small"
                            variant={typeof option === "string" ? "outlined" : "filled"}
                          />
                        ))
                      }
                      renderOption={(props, option: any) => {
                        if (typeof option === "string") return null;
                        return (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body2">{option.name}</Typography>
                              {option.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {option.description}
                                </Typography>
                              )}
                            </Box>
                          </li>
                        );
                      }}
                      filterOptions={(options: any[], params: any) => {
                        const filtered = options.filter((option: any) =>
                          typeof option === "string"
                            ? false
                            : option.name
                                .toLowerCase()
                                .includes(params.inputValue.toLowerCase())
                        );
                        if (
                          params.inputValue !== "" &&
                          !filtered.some(
                            (o: any) =>
                              typeof o !== "string" &&
                              o.name.toLowerCase() ===
                                params.inputValue.toLowerCase()
                          )
                        ) {
                          filtered.push(params.inputValue);
                        }
                        return filtered;
                      }}
                    />
                  );
                }}
              />
            )}
          </Box>
        </Grid>

        {/* Display LLM Notes if available */}
        {(llmNotes ||
          llmEntityNotes ||
          llmEntityTypeNotes ||
          llmAsOfDateNotes) && (
          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: "rgba(201,186,232,0.3)",
                borderRadius: 1,
                p: 2,
                border: "1px solid rgba(156, 39, 176, 0.3)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: aiNotesExpanded ? 1 : 0,
                  cursor: "pointer",
                }}
                onClick={() => setAiNotesExpanded(!aiNotesExpanded)}
              >
                <AutoAwesomeIcon
                  sx={{
                    color: "rgba(156, 39, 176, 0.8)",
                    fontSize: "18px",
                    mr: 1,
                  }}
                />
                <Typography variant="subtitle2" color="text.secondary">
                  AI Analysis Notes
                </Typography>
                <IconButton
                  size="small"
                  sx={{ ml: "auto", color: "rgba(156, 39, 176, 0.8)" }}
                >
                  {aiNotesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={aiNotesExpanded}>
                {llmNotes && (
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: "medium", color: "text.primary" }}
                    >
                      Document Type:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {llmNotes}
                    </Typography>
                  </Box>
                )}
                {llmAsOfDateNotes && (
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: "medium", color: "text.primary" }}
                    >
                      Date Analysis:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {llmAsOfDateNotes}
                    </Typography>
                  </Box>
                )}
                {llmEntityNotes && (
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: "medium", color: "text.primary" }}
                    >
                      Entity Analysis:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {llmEntityNotes}
                    </Typography>
                  </Box>
                )}
                {llmEntityTypeNotes && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: "medium", color: "text.primary" }}
                    >
                      Entity Type Analysis:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {llmEntityTypeNotes}
                    </Typography>
                  </Box>
                )}
              </Collapse>
            </Box>
          </Grid>
        )}

        <Grid item xs={4}>
          <FormControlLabel
            control={
              <Controller
                name={`queues.${index}.allowDuplicateFile`}
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value || false}
                    onChange={field.onChange}
                  />
                )}
              />
            }
            label="Allow Duplicate File"
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default DocumentRowForm;
